import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { Check, ChevronDown, ChevronUp, Edit2, X, Mail, XCircle, CreditCard, AlertCircle } from 'lucide-react';
import {
  PaymentPlanDto,
  PaymentInstallmentInfo,
  PaymentPlanStatus,
  PaymentPlanSetupMethod,
  formatCurrency,
  formatDate,
  getPaymentPlanStatusColor,
  paymentPlansApi,
  handleApiError
} from '../../services/paymentsService';
import { hexToRgba } from '../../utils/colorUtils';
import ConfirmInstallmentModal from '../modals/ConfirmInstallmentModal';
import CancelPaymentPlanModal from '../modals/CancelPaymentPlanModal';
import UpdateCardModal from '../modals/UpdateCardModal';
import { useTenantConfig } from '../../contexts/TenantConfigContext';

interface PaymentPlanSectionProps {
  paymentPlan: PaymentPlanDto;
  onPlanChanged: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const Header = styled.div`
  padding: 24px;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const StatusBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: var(--border-radius-sm);
  font-size: 11px;
  font-weight: 600;
  background: ${props => hexToRgba(props.$color, 0.12)};
  color: ${props => props.$color};
`;

// Main info cards
const InfoCardsRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const InfoCard = styled.div`
  flex: 1;
  padding: 16px;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
`;

const InfoCardLabel = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 8px;
`;

const InfoCardValue = styled.div<{ $large?: boolean; $color?: string }>`
  font-size: ${props => props.$large ? '24px' : '16px'};
  font-weight: 700;
  color: ${props => props.$color || 'var(--text-primary)'};
  direction: ltr;
  text-align: right;
`;

const InfoCardSubtext = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
`;

// Next payment highlight
const NextPaymentCard = styled.div`
  background: linear-gradient(135deg, var(--warning-light) 0%, #fff8e6 100%);
  border: 1px solid #d97706;
  border-radius: var(--border-radius-md);
  padding: 16px 20px;
  margin-bottom: 20px;
`;

const NextPaymentHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #d97706;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const NextPaymentDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }
`;

const NextPaymentDate = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
`;

const NextPaymentAmount = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #d97706;
  direction: ltr;
`;

// Progress section
const ProgressSection = styled.div`
  margin-bottom: 8px;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ProgressLabel = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const ProgressPercent = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--primary-color);
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: var(--border-color);
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $percentage: number }>`
  height: 100%;
  width: ${props => props.$percentage}%;
  background: linear-gradient(90deg, var(--primary-color), #16a34a);
  transition: width 0.3s ease;
`;

// Table Section - Collapsible
const TableSection = styled.div`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  overflow: hidden;
`;

const TableHeader = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--background-secondary);
  border-radius: ${props => props.$isExpanded ? 'var(--border-radius-md) var(--border-radius-md) 0 0' : 'var(--border-radius-md)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover-background);
  }

  @media (max-width: 767px) {
    padding: 12px 16px;
  }
`;

const TableHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const TableHeaderCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const ChevronWrapper = styled.div`
  color: var(--text-secondary);
`;

const TableContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '1000px' : '0'};
  overflow: ${props => props.$isExpanded ? 'auto' : 'hidden'};
  transition: max-height 0.3s ease;
  background: var(--surface-color);
  border-top: ${props => props.$isExpanded ? '1px solid var(--border-color)' : 'none'};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  @media (max-width: 767px) {
    font-size: 13px;
  }
`;

const Th = styled.th`
  text-align: center;
  padding: 12px 16px;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 12px;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-secondary);

  @media (max-width: 767px) {
    padding: 10px 12px;
  }
`;

const Td = styled.td`
  text-align: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);

  @media (max-width: 767px) {
    padding: 10px 12px;
  }
`;

const AmountTd = styled(Td)`
  direction: ltr;
  font-weight: 500;
`;

const Tr = styled.tr`
  &:last-child td {
    border-bottom: none;
  }

  &:hover {
    background: var(--hover-background);
  }
`;

const InstallmentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 10px;
  font-weight: 600;
  background: ${hexToRgba('#d97706', 0.1)};
  color: #d97706;
`;

const FailedInstallmentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 10px;
  font-weight: 600;
  background: ${hexToRgba('#DC2626', 0.1)};
  color: #DC2626;
`;

const OverdueInstallmentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 10px;
  font-weight: 600;
  background: ${hexToRgba('#DC2626', 0.1)};
  color: #DC2626;
`;

const FailureAlert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
  background: #FEF2F2;
  border: 1px solid #DC2626;
  border-radius: var(--border-radius-md);
  color: #991B1B;
`;

const FailureIconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-top: 2px;
`;

const FailureContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const FailureTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 4px;
  color: #991B1B;
`;

const FailureMessage = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: #7F1D1D;
`;

const FailureMeta = styled.div`
  font-size: 11px;
  color: #B91C1C;
  margin-top: 6px;
`;

const FailedTr = styled.tr`
  background: ${hexToRgba('#DC2626', 0.05)};

  & > td {
    color: #991B1B;
  }
`;

// New button design - outline style with icon
const ConfirmButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 1px solid #16a34a;
  color: #16a34a;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: var(--border-radius-sm);
  transition: all 0.2s ease;

  &:hover {
    background: #16a34a;
    color: white;
  }

  svg {
    flex-shrink: 0;
  }
`;

const EmptyTableState = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 13px;
`;

// ---------- Edit Panel ----------

const EditPanel = styled.div`
  margin-top: 20px;
  padding: 16px 20px;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);

  @media (max-width: 767px) {
    padding: 14px 16px;
  }
`;

const EditPanelTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 14px;
`;

const EditRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-color);

  &:last-of-type {
    border-bottom: none;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const EditRowLabel = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  flex-shrink: 0;
  min-width: 120px;
`;

const EditRowValue = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  direction: ltr;
  text-align: left;
  flex: 1;

  @media (max-width: 767px) {
    text-align: right;
  }
`;

const EditRowControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InlineInput = styled.input`
  width: 120px;
  padding: 6px 10px;
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-family: inherit;
  direction: ltr;
  text-align: left;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 115, 234, 0.1);
  }
`;

const IconButton = styled.button<{ $variant?: 'primary' | 'success' | 'cancel' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid;

  ${props => props.$variant === 'success' ? `
    background: #16a34a;
    color: white;
    border-color: #16a34a;
    &:hover:not(:disabled) { background: #15803d; }
  ` : props.$variant === 'cancel' ? `
    background: white;
    color: var(--text-secondary);
    border-color: var(--border-color);
    &:hover:not(:disabled) { background: var(--background-secondary); }
  ` : `
    background: white;
    color: var(--primary-color);
    border-color: var(--primary-color);
    &:hover:not(:disabled) { background: var(--primary-color); color: white; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  border-radius: var(--border-radius-sm);
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--background-secondary);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InlineErrorText = styled.div`
  font-size: 12px;
  color: #dc2626;
  margin-top: 4px;
`;

// ---------- Actions Panel ----------

const ActionsPanel = styled.div`
  margin-top: 16px;
  padding: 14px 20px;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    padding: 12px 16px;
    flex-direction: column;
  }
`;

const ActionButton = styled.button<{ $variant?: 'danger' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: white;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border-radius: var(--border-radius-sm);
  transition: all 0.15s ease;

  ${props => props.$variant === 'danger' ? `
    border: 1px solid #dc2626;
    color: #dc2626;
    &:hover:not(:disabled) { background: #dc2626; color: white; }
  ` : `
    border: 1px solid var(--primary-color);
    color: var(--primary-color);
    &:hover:not(:disabled) { background: var(--primary-color); color: white; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    width: 100%;
    justify-content: center;
  }
`;

const PaymentPlanSection: React.FC<PaymentPlanSectionProps> = ({
  paymentPlan,
  onPlanChanged
}) => {
  const { config } = useTenantConfig();
  const cardcomEnabled = !!config?.cardcomEnabled;

  const [selectedInstallment, setSelectedInstallment] = useState<PaymentInstallmentInfo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTableExpanded, setIsTableExpanded] = useState(true); // Default open
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isUpdateCardModalOpen, setIsUpdateCardModalOpen] = useState(false);

  // Inline edit state
  const [editingAmount, setEditingAmount] = useState(false);
  const [amountInput, setAmountInput] = useState(String(paymentPlan.installmentAmount));
  const [amountError, setAmountError] = useState<string | null>(null);
  const [savingAmount, setSavingAmount] = useState(false);

  const [editingDay, setEditingDay] = useState(false);
  const [dayInput, setDayInput] = useState(String(paymentPlan.dayOfMonth ?? ''));
  const [dayError, setDayError] = useState<string | null>(null);
  const [savingDay, setSavingDay] = useState(false);

  const [sendingEmail, setSendingEmail] = useState(false);

  const handleConfirmClick = (installment: PaymentInstallmentInfo) => {
    setSelectedInstallment(installment);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedInstallment(null);
  };

  const handleConfirmSuccess = () => {
    setIsModalOpen(false);
    setSelectedInstallment(null);
    onPlanChanged();
  };

  const startEditAmount = () => {
    setAmountInput(String(paymentPlan.installmentAmount));
    setAmountError(null);
    setEditingAmount(true);
  };

  const cancelEditAmount = () => {
    setEditingAmount(false);
    setAmountError(null);
  };

  const saveAmount = async () => {
    const value = parseFloat(amountInput);
    if (!Number.isFinite(value) || value <= 0) {
      setAmountError('יש להזין סכום גדול מאפס');
      return;
    }
    setSavingAmount(true);
    setAmountError(null);
    try {
      await paymentPlansApi.updateAmount(paymentPlan.id, value);
      toast.success('הסכום עודכן בהצלחה');
      setEditingAmount(false);
      onPlanChanged();
    } catch (error) {
      setAmountError(handleApiError(error));
    } finally {
      setSavingAmount(false);
    }
  };

  const startEditDay = () => {
    setDayInput(String(paymentPlan.dayOfMonth ?? ''));
    setDayError(null);
    setEditingDay(true);
  };

  const cancelEditDay = () => {
    setEditingDay(false);
    setDayError(null);
  };

  const saveDay = async () => {
    const value = parseInt(dayInput, 10);
    if (!Number.isInteger(value) || value < 1 || value > 28) {
      setDayError('יום החיוב חייב להיות מספר שלם בטווח 1-28');
      return;
    }
    setSavingDay(true);
    setDayError(null);
    try {
      await paymentPlansApi.updateDayOfMonth(paymentPlan.id, value);
      toast.success('יום החיוב עודכן בהצלחה');
      setEditingDay(false);
      onPlanChanged();
    } catch (error) {
      setDayError(handleApiError(error));
    } finally {
      setSavingDay(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await paymentPlansApi.sendCardUpdateEmail(paymentPlan.id);
      toast.success('המייל נשלח ללקוח בהצלחה.');
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setSendingEmail(false);
    }
  };

  // פקדי Cardcom (עריכת סכום/יום, עדכון כרטיס, שליחת מייל) מוצגים רק כש-Cardcom מופעל לחברה
  const isActiveCardcomPlan = cardcomEnabled
    && paymentPlan.status === PaymentPlanStatus.Active
    && paymentPlan.setupMethod === PaymentPlanSetupMethod.Cardcom
    && !!paymentPlan.cardcomMasterRecurringId;

  const isActivePlan = paymentPlan.status === PaymentPlanStatus.Active;

  const progressPercentage = paymentPlan.totalAmount > 0
    ? (paymentPlan.amountPaid / paymentPlan.totalAmount) * 100
    : 0;

  // Filter to show only pending installments
  const pendingInstallments = paymentPlan.installments?.filter(i => !i.isPaid) || [];

  return (
    <>
      <Container>
        <Header>
          <TitleRow>
            <Title>תוכנית תשלומים</Title>
            <StatusBadge $color={getPaymentPlanStatusColor(paymentPlan.status)}>
              {paymentPlan.statusName}
            </StatusBadge>
          </TitleRow>

          {/* Main Info Cards */}
          <InfoCardsRow>
            <InfoCard>
              <InfoCardLabel>סה"כ לתשלום</InfoCardLabel>
              <InfoCardValue $large>{formatCurrency(paymentPlan.totalAmount)}</InfoCardValue>
              <InfoCardSubtext>{paymentPlan.numberOfInstallments} תשלומים</InfoCardSubtext>
            </InfoCard>
            <InfoCard>
              <InfoCardLabel>שולם עד כה</InfoCardLabel>
              <InfoCardValue $large $color="#16a34a">
                {formatCurrency(paymentPlan.amountPaid)}
              </InfoCardValue>
              <InfoCardSubtext>נותר: {formatCurrency(paymentPlan.remainingAmount)}</InfoCardSubtext>
            </InfoCard>
          </InfoCardsRow>

          {/* Next Payment Highlight */}
          {paymentPlan.nextPaymentDate && (
            <NextPaymentCard>
              <NextPaymentHeader>תשלום הבא</NextPaymentHeader>
              <NextPaymentDetails>
                <NextPaymentDate>{formatDate(paymentPlan.nextPaymentDate)}</NextPaymentDate>
                <NextPaymentAmount>{formatCurrency(paymentPlan.installmentAmount)}</NextPaymentAmount>
              </NextPaymentDetails>
            </NextPaymentCard>
          )}

          {/* Progress Bar */}
          <ProgressSection>
            <ProgressHeader>
              <ProgressLabel>התקדמות</ProgressLabel>
              <ProgressPercent>{progressPercentage.toFixed(0)}%</ProgressPercent>
            </ProgressHeader>
            <ProgressBar>
              <ProgressFill $percentage={progressPercentage} />
            </ProgressBar>
          </ProgressSection>

          {/* Edit Panel — only for active Cardcom plans */}
          {isActiveCardcomPlan && (
            <EditPanel>
              <EditPanelTitle>עריכת תכנית הוראת קבע</EditPanelTitle>

              {/* Monthly amount */}
              <EditRow>
                <EditRowLabel>סכום חודשי</EditRowLabel>
                {editingAmount ? (
                  <EditRowControls>
                    <InlineInput
                      type="number"
                      step="0.01"
                      min="0"
                      value={amountInput}
                      onChange={(e) => setAmountInput(e.target.value)}
                      disabled={savingAmount}
                      autoFocus
                    />
                    <IconButton $variant="success" onClick={saveAmount} disabled={savingAmount} aria-label="שמור">
                      <Check size={14} />
                    </IconButton>
                    <IconButton $variant="cancel" onClick={cancelEditAmount} disabled={savingAmount} aria-label="ביטול">
                      <X size={14} />
                    </IconButton>
                  </EditRowControls>
                ) : (
                  <EditRowControls>
                    <EditRowValue>{formatCurrency(paymentPlan.installmentAmount)}</EditRowValue>
                    <EditButton onClick={startEditAmount}>
                      <Edit2 size={13} />
                      ערוך
                    </EditButton>
                  </EditRowControls>
                )}
              </EditRow>
              {amountError && <InlineErrorText>{amountError}</InlineErrorText>}

              {/* Day of month */}
              <EditRow>
                <EditRowLabel>יום חיוב בחודש</EditRowLabel>
                {editingDay ? (
                  <EditRowControls>
                    <InlineInput
                      type="number"
                      min="1"
                      max="28"
                      value={dayInput}
                      onChange={(e) => setDayInput(e.target.value)}
                      disabled={savingDay}
                      autoFocus
                    />
                    <IconButton $variant="success" onClick={saveDay} disabled={savingDay} aria-label="שמור">
                      <Check size={14} />
                    </IconButton>
                    <IconButton $variant="cancel" onClick={cancelEditDay} disabled={savingDay} aria-label="ביטול">
                      <X size={14} />
                    </IconButton>
                  </EditRowControls>
                ) : (
                  <EditRowControls>
                    <EditRowValue>
                      {paymentPlan.dayOfMonth ? `יום ${paymentPlan.dayOfMonth} בחודש` : '-'}
                    </EditRowValue>
                    <EditButton onClick={startEditDay}>
                      <Edit2 size={13} />
                      ערוך
                    </EditButton>
                  </EditRowControls>
                )}
              </EditRow>
              {dayError && <InlineErrorText>{dayError}</InlineErrorText>}

              {/* Update card */}
              <EditRow>
                <EditRowLabel>פרטי כרטיס</EditRowLabel>
                <EditRowControls>
                  <EditButton onClick={() => setIsUpdateCardModalOpen(true)}>
                    <CreditCard size={13} />
                    עדכן פרטי כרטיס
                  </EditButton>
                </EditRowControls>
              </EditRow>
            </EditPanel>
          )}

          {/* Actions Panel — only for active plans */}
          {isActivePlan && (
            <ActionsPanel>
              {isActiveCardcomPlan && (
                <ActionButton onClick={handleSendEmail} disabled={sendingEmail}>
                  <Mail size={14} />
                  {sendingEmail ? 'שולח...' : 'שלח מייל עדכון כרטיס'}
                </ActionButton>
              )}
              <ActionButton $variant="danger" onClick={() => setIsCancelModalOpen(true)}>
                <XCircle size={14} />
                סגור תוכנית
              </ActionButton>
            </ActionsPanel>
          )}
        </Header>

        {/* Active charge failure alert (DEBTAUTOBILLING or other retry-pending failure) */}
        {paymentPlan.lastChargeFailureMessage && (
          <FailureAlert>
            <FailureIconWrap>
              <AlertCircle size={20} color="#DC2626" />
            </FailureIconWrap>
            <FailureContent>
              <FailureTitle>
                {paymentPlan.lastChargeFailureInstallmentNumber
                  ? `הסליקה של תשלום #${paymentPlan.lastChargeFailureInstallmentNumber} נכשלה`
                  : 'הסליקה האחרונה נכשלה'}
              </FailureTitle>
              <FailureMessage>{paymentPlan.lastChargeFailureMessage}</FailureMessage>
              {paymentPlan.lastChargeFailureAt && (
                <FailureMeta>
                  נכשל ב-{formatDate(paymentPlan.lastChargeFailureAt)}
                  {paymentPlan.lastChargeFailureAttempt && ` · ניסיון ${paymentPlan.lastChargeFailureAttempt}/7`}
                </FailureMeta>
              )}
            </FailureContent>
          </FailureAlert>
        )}

        {/* Installments Table - Collapsible */}
        <TableSection>
          <TableHeader $isExpanded={isTableExpanded} onClick={() => setIsTableExpanded(!isTableExpanded)}>
            <TableHeaderTitle>
              תשלומים עתידיים
              <TableHeaderCount>({pendingInstallments.length})</TableHeaderCount>
            </TableHeaderTitle>
            <ChevronWrapper>
              {isTableExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </ChevronWrapper>
          </TableHeader>

          <TableContent $isExpanded={isTableExpanded}>
            {pendingInstallments.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <Th>#</Th>
                    <Th>תאריך</Th>
                    <Th>סכום</Th>
                    <Th>סטטוס</Th>
                    <Th>פעולה</Th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInstallments.map((installment) => {
                    const isFailed = paymentPlan.lastChargeFailureInstallmentNumber === installment.installmentNumber
                      && !!paymentPlan.lastChargeFailureMessage;
                    const RowComponent = isFailed ? FailedTr : Tr;
                    return (
                      <RowComponent key={installment.installmentNumber}>
                        <Td>{installment.installmentNumber}</Td>
                        <Td>{formatDate(installment.dueDate)}</Td>
                        <AmountTd>
                          {formatCurrency(installment.amount)}
                        </AmountTd>
                        <Td>
                          {isFailed ? (
                            <FailedInstallmentBadge title={paymentPlan.lastChargeFailureMessage}>
                              <AlertCircle size={12} />
                              סליקה נכשלה
                            </FailedInstallmentBadge>
                          ) : installment.isOverdue ? (
                            <OverdueInstallmentBadge>
                              <AlertCircle size={12} />
                              בפיגור
                            </OverdueInstallmentBadge>
                          ) : (
                            <InstallmentBadge>ממתין</InstallmentBadge>
                          )}
                        </Td>
                        <Td>
                          <ConfirmButton onClick={() => handleConfirmClick(installment)}>
                            <Check size={12} />
                            אשר
                          </ConfirmButton>
                        </Td>
                      </RowComponent>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <EmptyTableState>
                כל התשלומים בתוכנית שולמו
              </EmptyTableState>
            )}
          </TableContent>
        </TableSection>
      </Container>

      {/* Confirm Installment Modal */}
      {selectedInstallment && (
        <ConfirmInstallmentModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          paymentPlan={paymentPlan}
          installment={selectedInstallment}
          onSuccess={handleConfirmSuccess}
        />
      )}

      {/* Cancel Payment Plan Modal */}
      <CancelPaymentPlanModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        paymentPlan={paymentPlan}
        onSuccess={onPlanChanged}
      />

      {/* Update Card Modal */}
      <UpdateCardModal
        isOpen={isUpdateCardModalOpen}
        onClose={() => setIsUpdateCardModalOpen(false)}
        paymentPlan={paymentPlan}
        onSuccess={onPlanChanged}
      />
    </>
  );
};

export default PaymentPlanSection;
